import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import reportService from '../../services/ReportService.js';

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `Rs. ${Math.round(num).toLocaleString('en-IN')}`;
};

const SUMMARY_LABELS = {
  gcs: 'GCs',
  paid: 'Paid',
  to_pay: 'To-Pay',
  on_account: 'On Account',
  loading: 'Loading',
  unloading: 'Unloading',
  other: 'Other',
  total: 'Total',
};

const FieldGrid = ({ title, rows }) => {
  if (!rows?.length) return null;
  return (
    <section className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-600" style={{ background: '#E8F0FE' }}>
        {title}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 px-3 py-3">
        {rows.map((row) => (
          <div key={row.label} className="min-w-0">
            <dt className="text-[10px] font-semibold text-slate-400">{row.label}</dt>
            <dd className="text-sm font-medium text-slate-800 break-words">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const Shell = ({ title, subtitle, onClose, zIndex, children }) => (
  <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex }}>
    <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-900/45" onClick={onClose} />
    <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3" style={{ background: '#F8FAFC' }}>
        <div className="min-w-0">
          <h2 className="m-0 text-base font-extrabold text-slate-900 break-all">{title}</h2>
          {subtitle ? <p className="m-0 mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-500 hover:bg-slate-200" aria-label="Close dialog">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="overflow-y-auto px-4 py-3 space-y-3">{children}</div>
    </div>
  </div>
);

const ShipmentBody = ({ data, onOpenMlSheet }) => (
  <>
    {data.ml_sheet_ids?.length > 0 && (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-sky-800">ML Sheet</span>
        {data.ml_sheet_ids.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => onOpenMlSheet(id)}
            className="text-sm font-semibold text-blue-700 underline underline-offset-2"
          >
            {id}
          </button>
        ))}
      </div>
    )}
    {(data.from_location || data.to_location) && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {data.from_location && (
          <div className="rounded-xl border border-slate-200 px-3 py-2">
            <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">From</p>
            <p className="m-0 text-sm font-semibold text-slate-800">{data.from_location}</p>
          </div>
        )}
        {data.to_location && (
          <div className="rounded-xl border border-slate-200 px-3 py-2">
            <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">To</p>
            <p className="m-0 text-sm font-semibold text-slate-800">{data.to_location}</p>
          </div>
        )}
      </div>
    )}
    <FieldGrid title="Sender" rows={data.sender} />
    <FieldGrid title="Receiver" rows={data.receiver} />
    {(data.charges?.length > 0 || data.total != null) && (
      <section className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-600" style={{ background: '#E8F0FE' }}>
          Charges
        </div>
        <div className="divide-y divide-slate-100">
          {(data.charges || []).map((line) => (
            <div key={line.label} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="text-slate-600">{line.label}</span>
              <span className="font-semibold tabular-nums text-slate-900">{money(line.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm" style={{ background: '#D6E3F8' }}>
            <span className="font-bold text-slate-900">Total</span>
            <span className="font-extrabold tabular-nums text-slate-900">{money(data.total)}</span>
          </div>
        </div>
      </section>
    )}
  </>
);

const MlSheetBody = ({ data, onOpenShipment }) => {
  const summary = data.summary || {};
  const keys = data.charge_keys || [];
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(summary).map(([key, value]) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
            <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">{SUMMARY_LABELS[key] || key}</p>
            <p className="m-0 text-sm font-extrabold text-slate-900 tabular-nums">
              {key === 'gcs' ? Number(value || 0).toLocaleString('en-IN') : money(value)}
            </p>
          </div>
        ))}
      </div>
      {(data.from_locations?.length > 0 || data.to_locations?.length > 0 || data.vehicle_number) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          {data.vehicle_number && (
            <div className="rounded-xl border border-slate-200 px-3 py-2">
              <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">Vehicle</p>
              <p className="m-0 font-semibold text-slate-800">{data.vehicle_number}</p>
            </div>
          )}
          {data.from_locations?.length > 0 && (
            <div className="rounded-xl border border-slate-200 px-3 py-2">
              <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">From</p>
              <p className="m-0 font-semibold text-slate-800">{data.from_locations.join(', ')}</p>
            </div>
          )}
          {data.to_locations?.length > 0 && (
            <div className="rounded-xl border border-slate-200 px-3 py-2">
              <p className="m-0 text-[10px] font-semibold uppercase text-slate-400">To</p>
              <p className="m-0 font-semibold text-slate-800">{data.to_locations.join(', ')}</p>
            </div>
          )}
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[640px] border-collapse text-xs">
          <thead>
            <tr>
              {(data.columns || []).map((col) => (
                <th key={col} className="px-2 py-2 text-left font-bold whitespace-nowrap border-b border-sky-200" style={{ background: '#D6E3F8', color: '#1F4E79' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.shipments || []).map((row) => (
              <tr key={row.shipment_id} className="odd:bg-white even:bg-slate-50">
                <td className="px-2 py-1.5 border-b border-slate-100 whitespace-nowrap">
                  <button type="button" className="font-semibold text-blue-700 underline" onClick={() => onOpenShipment(row.shipment_id)}>
                    {row.shipment_id}
                  </button>
                </td>
                <td className="px-2 py-1.5 border-b border-slate-100">{row.from_location || '—'}</td>
                <td className="px-2 py-1.5 border-b border-slate-100">{row.to_location || '—'}</td>
                <td className="px-2 py-1.5 border-b border-slate-100">{row.sender_name || '—'}</td>
                <td className="px-2 py-1.5 border-b border-slate-100">{row.receiver_name || '—'}</td>
                {keys.map((key) => (
                  <td key={key} className="px-2 py-1.5 border-b border-slate-100 text-right tabular-nums">{money(row.charges?.[key])}</td>
                ))}
                <td className="px-2 py-1.5 border-b border-slate-100 text-right font-semibold tabular-nums">{money(row.total)}</td>
              </tr>
            ))}
            {(data.shipments || []).length === 0 && (
              <tr>
                <td colSpan={(data.columns || []).length || 1} className="px-3 py-6 text-center text-slate-400">
                  No shipments on this ML sheet.
                </td>
              </tr>
            )}
          </tbody>
          {(data.shipments || []).length > 0 && (
            <tfoot>
              <tr style={{ background: '#B4C7E7' }}>
                <td className="px-2 py-2 font-bold" colSpan={5}>TOTAL</td>
                {keys.map((key) => (
                  <td key={key} className="px-2 py-2 text-right font-bold tabular-nums">{money(data.totals?.[key])}</td>
                ))}
                <td className="px-2 py-2 text-right font-extrabold tabular-nums">{money(data.totals?.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
};

const usePreview = (id, loader) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setData(null);
      setError('');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    loader(id)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err?.response?.data?.error || 'Could not load this record');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, loader]);

  return { data, error, loading };
};

const ReportRecordModals = ({
  shipmentId,
  mlSheetId,
  onCloseShipment,
  onCloseMlSheet,
  onOpenMlSheet,
  onOpenShipment,
}) => {
  const shipment = usePreview(shipmentId, reportService.getShipmentPreview);
  const mlSheet = usePreview(mlSheetId, reportService.getMlSheetPreview);
  const [front, setFront] = useState('shipment');
  const openMl = (id) => {
    setFront('ml');
    onOpenMlSheet(id);
  };
  const openShip = (id) => {
    setFront('shipment');
    onOpenShipment(id);
  };

  return (
    <>
      {shipmentId && (
        <Shell
          zIndex={front === 'shipment' ? 90 : 70}
          title={shipment.data?.shipment_id || shipmentId}
          subtitle={shipment.data?.booked_at ? `Booked ${shipment.data.booked_at}` : ''}
          onClose={onCloseShipment}
        >
          {shipment.loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading shipment…
            </div>
          )}
          {shipment.error && <p className="text-sm text-red-600">{shipment.error}</p>}
          {shipment.data && <ShipmentBody data={shipment.data} onOpenMlSheet={openMl} />}
        </Shell>
      )}
      {mlSheetId && (
        <Shell
          zIndex={front === 'ml' ? 90 : 70}
          title={mlSheet.data?.ml_sheet_id || mlSheetId}
          subtitle={[mlSheet.data?.status, mlSheet.data?.created_at].filter(Boolean).join(' · ')}
          onClose={onCloseMlSheet}
        >
          {mlSheet.loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading ML sheet…
            </div>
          )}
          {mlSheet.error && <p className="text-sm text-red-600">{mlSheet.error}</p>}
          {mlSheet.data && <MlSheetBody data={mlSheet.data} onOpenShipment={openShip} />}
        </Shell>
      )}
    </>
  );
};

export default ReportRecordModals;
